import { createFileRoute } from '@tanstack/react-router';
import { AnalyticsPage } from '@/features/creators/dashboard/analytics/analytics-page';
import { TabLoadError } from '@/features/creators/dashboard/components/load-error';
import {
	ANALYTICS_TEXT,
	LOAD_ERROR_TEXT,
} from '@/features/creators/dashboard/content';
import { fetchCreatorAnalytics } from '@/features/creators/dashboard/services/fetch-creator-analytics';
import {
	ANALYTICS_PERIODS,
	type AnalyticsPeriod,
	type AnalyticsSelection,
} from '@/features/creators/dashboard/types';
import {
	DEFAULT_ANALYTICS_PERIOD,
	isAllowedRange,
	selectionRange,
} from '@/features/creators/dashboard/utils/analytics-period';
import { normalizeEnum } from '@/features/creators/shared/normalize';
import { PageLoader } from '@/features/creators/shared/page-loader';

interface AnalyticsSearch {
	/** A preset; left out for the default, the last 30 days. */
	period?: AnalyticsPeriod;
	/** Custom dates (yyyy-MM-dd), both or neither. */
	from?: string;
	to?: string;
}

const text = (value: unknown) => (typeof value === 'string' ? value : '');

function toSelection({
	period,
	from,
	to,
}: AnalyticsSearch): AnalyticsSelection {
	return from && to
		? { period: 'custom', range: { from, to } }
		: { period: period ?? DEFAULT_ANALYTICS_PERIOD };
}

function toSearch(selection: AnalyticsSelection): AnalyticsSearch {
	if (selection.period === 'custom') return { ...selection.range };
	return selection.period === DEFAULT_ANALYTICS_PERIOD
		? {}
		: { period: selection.period };
}

// /ixi-finance/creators/dashboard/analytics — the Analytics tab. The period
// picked is in the URL (?period=90d, or ?from=…&to=… for custom dates), so it
// survives a reload and Back; each period's numbers load here.
export const Route = createFileRoute(
	'/_auth/ixi-finance/creators_/dashboard/analytics',
)({
	// Custom dates outside what the picker allows (an old link, a hand-edited
	// URL) fall back to the preset, as the API would refuse them.
	validateSearch: (search: Record<string, unknown>): AnalyticsSearch => {
		const range = { from: text(search.from), to: text(search.to) };
		if (isAllowedRange(range)) return range;
		const period = normalizeEnum(text(search.period), ANALYTICS_PERIODS);
		return period ? { period } : {};
	},
	loaderDeps: ({ search }) => toSelection(search),
	loader: ({ deps }) => fetchCreatorAnalytics(selectionRange(deps)),
	component: AnalyticsRoute,
	pendingComponent: PageLoader,
	errorComponent: () => (
		<TabLoadError
			page={ANALYTICS_TEXT.title}
			what={LOAD_ERROR_TEXT.analytics}
		/>
	),
	// 'stay' means the URL hash changed without a route transition (the
	// custom range sheet opening or closing). Skip the reload so the numbers
	// don't refetch; picking a period changes the deps, which still loads.
	shouldReload: ({ cause }) => cause !== 'stay',
});

function AnalyticsRoute() {
	const navigate = Route.useNavigate();
	return (
		<AnalyticsPage
			analytics={Route.useLoaderData()}
			selection={toSelection(Route.useSearch())}
			onSelectionChange={(next) =>
				navigate({ search: toSearch(next), replace: true })
			}
		/>
	);
}
